import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Columns2, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { swatchFor } from '../lib/colors';

// Side-by-side shade comparison. Shoppers pin two colors and view their textures together before choosing.
const ShadeCompare = ({ colors = [], imageFor, onChoose, variant = 'pdp', lengths = [], selectedLength }) => {
  const [open, setOpen] = useState(false);
  const [slotA, setSlotA] = useState(colors[0] || null);
  const [slotB, setSlotB] = useState(colors[1] || colors[0] || null);
  const [active, setActive] = useState('A');
  const [len, setLen] = useState(selectedLength || lengths[0] || null);

  if (colors.length < 2) return null;

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const pick = (c) => {
    if (active === 'A') { setSlotA(c); setActive('B'); }
    else { setSlotB(c); setActive('A'); }
  };

  const Panel = ({ slot, shade, onFocus }) => (
    <button
      type="button"
      onClick={(e) => { stop(e); onFocus(); }}
      data-testid={`compare-slot-${slot}`}
      className={`flex-1 min-w-0 text-left rounded-lg overflow-hidden border transition-colors ${active === slot ? 'border-[#c6a15b] ring-2 ring-[#c6a15b]/40' : 'border-[#e0d6c3]'}`}
    >
      <div className="relative aspect-square bg-[#ece5d8] overflow-hidden">
        {imageFor(shade) ? (
          <img src={imageFor(shade)} alt={shade} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: swatchFor(shade) }} />
        )}
        {len && (
          <span className="absolute bottom-2 right-2 bg-[#0b0b0b]/85 text-[#e7d3a1] font-body text-[0.62rem] px-2 py-0.5 rounded-full" data-testid={`compare-len-badge-${slot}`}>{len}</span>
        )}
      </div>
      <div className="px-3 py-2 flex items-center gap-2">
        <span className="w-3.5 h-3.5 rounded-full border border-black/20 shrink-0" style={{ background: swatchFor(shade) }} />
        <span className="font-body text-xs text-[#1a1a1a] truncate">{shade}</span>
      </div>
    </button>
  );

  return (
    <div className={variant === 'pdp' ? 'mt-4' : 'mt-2 flex justify-center'} data-testid="shade-compare">
      {variant === 'pdp' ? (
        <button
          type="button"
          onClick={(e) => { stop(e); setOpen(true); }}
          data-testid="shade-compare-open"
          className="inline-flex items-center gap-2 px-6 py-3 border border-[#8a6a2c] text-[#5a3d1a] font-body text-[0.7rem] uppercase tracking-[0.15em] rounded-full shadow-sm hover:brightness-105 transition-[filter]"
          style={{ background: 'linear-gradient(135deg, #f5e7b8 0%, #d8b96a 22%, #c6a15b 48%, #b8860b 70%, #e7d3a1 100%)' }}
        >
          <Columns2 size={15} className="text-[#5a3d1a]" /> Compare Shades Side by Side
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => { stop(e); setOpen(true); }}
          data-testid={`shade-compare-open-${colors.length}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#8a6a2c] text-[#5a3d1a] font-body text-[0.6rem] uppercase tracking-wider rounded-full shadow-sm hover:brightness-105 transition-[filter]"
          style={{ background: 'linear-gradient(135deg, #f5e7b8 0%, #d8b96a 22%, #c6a15b 48%, #b8860b 70%, #e7d3a1 100%)' }}
        >
          <Columns2 size={12} className="text-[#5a3d1a]" /> Compare Shades
        </button>
      )}

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-[#0b0b0b]/70 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => { stop(e); setOpen(false); }}
            data-testid="shade-compare-overlay"
          >
            <motion.div
              className="w-full max-w-lg bg-[#faf6ef] rounded-xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
              initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
              onClick={stop}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#eadfca]">
                <h3 className="font-display text-2xl text-[#1a1a1a]">Compare Shades{len ? <span className="font-body text-sm text-[#a8823c] ml-2">at {len}</span> : ''}</h3>
                <button onClick={(e) => { stop(e); setOpen(false); }} data-testid="shade-compare-close" className="text-[#6b6459] hover:text-[#0b0b0b]"><X size={20} /></button>
              </div>

              <div className="p-5 overflow-y-auto">
                <div className="flex gap-4">
                  <Panel slot="A" shade={slotA} onFocus={() => setActive('A')} />
                  <Panel slot="B" shade={slotB} onFocus={() => setActive('B')} />
                </div>

                {lengths.length > 0 && (
                  <div className="mt-4">
                    <p className="overline text-[#1a1a1a] text-[0.58rem] mb-2">Preview at length — <span className="text-[#a8823c]">{len}</span></p>
                    <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                      {lengths.map((l) => (
                        <button key={l} type="button" onClick={(e) => { stop(e); setLen(l); }}
                          data-testid={`compare-length-${l}`}
                          className={`min-w-[42px] px-2.5 py-1.5 font-body text-[0.7rem] border rounded transition-colors ${len === l ? 'bg-[#0b0b0b] text-[#e7d3a1] border-[#0b0b0b]' : 'border-[#d8cdb8] text-[#4a463d] hover:border-[#c6a15b]'}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="mt-4 mb-2 font-body text-xs text-[#6b6459]">
                  Tap a shade below to load it into slot <span className="text-[#a8823c] font-medium">{active}</span>.
                </p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                  {colors.map((c) => {
                    const chosen = c === slotA || c === slotB;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={(e) => { stop(e); pick(c); }}
                        data-testid={`compare-chip-${c}`}
                        className={`flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border font-body text-xs transition-colors ${chosen ? 'border-[#c6a15b] bg-[#f2e7ce]' : 'border-[#d8cdb8] hover:border-[#c6a15b]'}`}
                      >
                        <span className="w-3.5 h-3.5 rounded-full border border-black/20 shrink-0" style={{ background: swatchFor(c) }} />
                        {c}
                      </button>
                    );
                  })}
                </div>

                {onChoose && (
                  <div className="mt-5 flex gap-3">
                    <button
                      onClick={(e) => { stop(e); onChoose(slotA); setOpen(false); }}
                      data-testid="compare-choose-a"
                      className="flex-1 py-3 border border-[#0b0b0b] text-[#0b0b0b] overline text-[0.6rem] hover:bg-[#0b0b0b] hover:text-[#e7d3a1] transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Check size={13} /> Choose {slotA}
                    </button>
                    <button
                      onClick={(e) => { stop(e); onChoose(slotB); setOpen(false); }}
                      data-testid="compare-choose-b"
                      className="flex-1 py-3 border border-[#0b0b0b] text-[#0b0b0b] overline text-[0.6rem] hover:bg-[#0b0b0b] hover:text-[#e7d3a1] transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Check size={13} /> Choose {slotB}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default ShadeCompare;
